import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';

const SupervisorDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    setLoading(true);
    try { const r = await api.get('/production/supervisor-dashboard'); setData(r.data); }
    catch(e) { console.error(e); } finally { setLoading(false); }
  };

  const approveEntry = async (id) => {
    try {
      await api.put(`/production/entries/${id}/approve`, { remarks: 'Approved by supervisor' });
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  if (!data) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>No data</div>;

  const { active_orders, pending_approval, planned_vs_actual } = data;
  const variancePct = planned_vs_actual.planned > 0 ? ((planned_vs_actual.actual / planned_vs_actual.planned) * 100).toFixed(1) : '0.0';

  return (
    <div data-testid="supervisor-dashboard-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Supervisor Dashboard</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-tertiary)', fontSize: '13px' }}>Reporting Layer - Approve entries and track planned vs actual</p>
        </div>
        <button onClick={fetchData} className="btn btn-secondary btn-sm" data-testid="refresh-btn"><RefreshCw size={14} /> Refresh</button>
      </div>

      <div className="grid grid-cols-4 mb-4">
        <div className="stat-card" style={{ borderLeft: '4px solid #0047AB' }} data-testid="stat-active-orders">
          <div className="stat-value">{active_orders.length}</div>
          <div className="stat-label">Active Orders</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #059669' }} data-testid="stat-planned">
          <div className="stat-value">{planned_vs_actual.planned}</div>
          <div className="stat-label">Total Planned</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #D97706' }} data-testid="stat-actual">
          <div className="stat-value">{planned_vs_actual.actual}</div>
          <div className="stat-label">Total Actual</div>
        </div>
        <div className="stat-card" style={{ borderLeft: variancePct >= 90 ? '4px solid #059669' : variancePct >= 70 ? '4px solid #D97706' : '4px solid #DC2626' }} data-testid="stat-achievement">
          <div className="stat-value" style={{ color: variancePct >= 90 ? '#059669' : variancePct >= 70 ? '#D97706' : '#DC2626' }}>{variancePct}%</div>
          <div className="stat-label">Achievement</div>
        </div>
      </div>

      {/* Planned vs Actual Progress */}
      <div className="card mb-4">
        <div className="card-header"><h3 className="card-title">Planned vs Actual</h3></div>
        <div style={{ height: '24px', background: 'var(--bg-surface-alt)', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}>
          <div style={{ height: '100%', width: `${Math.min(parseFloat(variancePct), 100)}%`, background: parseFloat(variancePct) >= 90 ? '#059669' : parseFloat(variancePct) >= 70 ? '#D97706' : '#DC2626', transition: 'width 300ms ease', borderRadius: '4px' }} />
          <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '12px', fontWeight: 600 }}>{planned_vs_actual.actual} / {planned_vs_actual.planned}</span>
        </div>
        <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
          Variance: {planned_vs_actual.variance} units remaining
        </div>
      </div>

      {/* Pending Approvals */}
      <div className="card mb-4">
        <div className="card-header flex items-center justify-between">
          <h3 className="card-title">Pending Approvals ({pending_approval.length})</h3>
          {pending_approval.length > 0 && <AlertTriangle size={18} color="#D97706" />}
        </div>
        {pending_approval.length === 0 ? (
          <p style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: '20px' }}>All entries approved</p>
        ) : (
          <div className="table-container">
            <table>
              <thead><tr><th>Date</th><th>Product</th><th>Machine</th><th>Operator</th><th>Good</th><th>Reject</th><th>Scrap</th><th>Action</th></tr></thead>
              <tbody>
                {pending_approval.map(e => (
                  <tr key={e.id} data-testid={`pending-row-${e.id}`}>
                    <td className="font-mono">{e.entry_date}</td>
                    <td>{e.product_sku}</td>
                    <td>{e.machine_name || '-'}</td>
                    <td>{e.operator_name || '-'}</td>
                    <td className="tabular-nums" style={{ color: '#059669' }}>{e.good_qty}</td>
                    <td className="tabular-nums" style={{ color: '#DC2626' }}>{e.reject_qty}</td>
                    <td className="tabular-nums" style={{ color: '#D97706' }}>{e.scrap_qty}</td>
                    <td>
                      <button onClick={() => approveEntry(e.id)} className="btn btn-primary btn-sm" data-testid={`approve-btn-${e.id}`}><CheckCircle size={14} /> Approve</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Active Orders */}
      <div className="card">
        <div className="card-header"><h3 className="card-title">Active Production Orders ({active_orders.length})</h3></div>
        {active_orders.length === 0 ? (
          <p style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: '20px' }}>No active orders</p>
        ) : (
          <div className="table-container">
            <table>
              <thead><tr><th>Order #</th><th>Product</th><th>Planned</th><th>Actual</th><th>Rejected</th><th>Progress</th><th>Status</th></tr></thead>
              <tbody>
                {active_orders.map(o => {
                  const pct = o.planned_qty > 0 ? ((o.actual_qty || 0) / o.planned_qty * 100).toFixed(0) : 0;
                  return (
                    <tr key={o.id} data-testid={`active-order-${o.order_number}`}>
                      <td className="font-mono">{o.order_number}</td>
                      <td>{o.product_name}</td>
                      <td className="tabular-nums">{o.planned_qty}</td>
                      <td className="tabular-nums">{o.actual_qty || 0}</td>
                      <td className="tabular-nums" style={{ color: '#DC2626' }}>{o.rejected_qty || 0}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ flex: 1, height: '8px', background: 'var(--bg-surface-alt)', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: '#0047AB', borderRadius: '4px' }} />
                          </div>
                          <span className="text-xs tabular-nums" style={{ minWidth: '36px' }}>{pct}%</span>
                        </div>
                      </td>
                      <td><span className={`badge ${o.status === 'in_progress' ? 'badge-info' : 'badge-warning'}`}>{o.status?.replace('_', ' ')}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupervisorDashboard;
